import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Modal,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useTranslation } from "react-i18next";
import { enqueueSnackbar } from "notistack";
import color from "../../Constants/colors";
import { useImportCompany } from "../../Query/useCompanies";
import { CompanyImportResult } from "../../Models/company";
import { useTerminology } from "../../Core/useTerminology";

interface CompanyImportModalProps {
  open: boolean;
  onClose: () => void;
  onImported?: (result: CompanyImportResult) => void;
}

const CompanyImportModal: React.FC<CompanyImportModalProps> = ({
  open,
  onClose,
  onImported,
}) => {
  const { t } = useTranslation("singleCompanyComponent");
  const importCompanyMutation = useImportCompany();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const isImporting = importCompanyMutation.isPending;
  const importFailed = importCompanyMutation.isError;
  const terminology = useTerminology();

  useEffect(() => {
    if (!open) {
      setSelectedFile(null);
      importCompanyMutation.reset();
    }
  }, [open, importCompanyMutation]);

  const handleClose = () => {
    if (isImporting) return;
    onClose();
  };

  const formatFileSize = (size?: number) => {
    if (!size && size !== 0) return "";
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
    if (importCompanyMutation.isError) {
      importCompanyMutation.reset();
    }
  };

  const handleImport = () => {
    if (!selectedFile) {
      enqueueSnackbar(t("companyImportExport.noFileSelected"), {
        variant: "warning",
      });
      return;
    }

    importCompanyMutation.mutate(selectedFile, {
      onSuccess: (result) => {
        onImported?.(result);
        onClose();
      },
    });
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          width: { xs: "90%", sm: 520 },
          backgroundColor: color.white,
          p: 4,
          borderRadius: 2,
          position: "relative",
          boxShadow: "0 18px 40px rgba(0, 0, 0, 0.2)",
        }}
      >
        <IconButton
          onClick={handleClose}
          disabled={isImporting}
          sx={{
            position: "absolute",
            top: 6,
            right: 6,
            color: color.primary_dark,
          }}
        >
          <CloseIcon />
        </IconButton>
        <Box
          display="flex"
          alignItems="center"
          gap={1.5}
          mb={2}
          sx={{
            pb: 1.5,
            borderBottom: `1px solid ${color.primary_light}`,
          }}
        >
          <Box>
            <Typography variant="h3" sx={{ color: color.primary_dark }}>
              {t("companyImportExport.title", {
                entity: terminology.singular,
              })}
            </Typography>
            <Typography variant="body2" sx={{ color: color.primary_dark }}>
              {t("companyImportExport.description", {
                entity: terminology.singular,
              })}
            </Typography>
          </Box>
        </Box>
        <input
          ref={fileInputRef}
          type="file"
          accept=".zip"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
        <Box
          sx={{
            border: `1px dashed ${color.accent}`,
            borderRadius: 2,
            p: 2,
            background: color.gray100,
            display: "grid",
            gridTemplateColumns: "1fr auto",
            alignItems: "center",
            gap: 2,
            mb: 2,
          }}
        >
          <Box
            sx={{
              minWidth: 0,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: color.primary_dark,
              }}
            >
              <UploadFileIcon sx={{ color: color.white, fontSize: 18 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{ color: color.primary_dark, fontWeight: 600 }}
              >
                {selectedFile?.name || t("companyImportExport.noFile")}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: color.primary_dark, opacity: 0.7 }}
              >
                {selectedFile
                  ? `${
                      selectedFile.type || "application/zip"
                    } • ${formatFileSize(selectedFile.size)}`
                  : t("companyImportExport.fileHint")}
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<UploadFileIcon />}
            onClick={handleFileSelect}
            disabled={isImporting}
            sx={{
              backgroundColor: color.accent,
              color: color.white,
              whiteSpace: "nowrap",
              "&:hover": {
                backgroundColor: color.primary_dark,
              },
            }}
          >
            {t("companyImportExport.selectZip")}
          </Button>
        </Box>
        <Box sx={{ mb: 3 }}>
          {isImporting && (
            <>
              <Typography
                variant="body2"
                sx={{ color: color.primary_dark, mb: 1 }}
              >
                {t("companyImportExport.importing")}
              </Typography>
              <LinearProgress
                sx={{
                  height: 8,
                  borderRadius: 8,
                  backgroundColor: color.primary_light,
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: color.accent,
                  },
                }}
              />
            </>
          )}
          {!isImporting && importFailed && (
            <Typography variant="body2" sx={{ color: color.red }}>
              {t("companyImportExport.importFailed")}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={isImporting}
            sx={{
              borderColor: color.secondary,
              color: color.primary_dark,
            }}
          >
            {t("companyImportExport.close")}
          </Button>
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={isImporting || !selectedFile}
            sx={{
              backgroundColor: color.accent,
              color: "white",
              "&:hover": {
                backgroundColor: color.primary_dark,
                color: color.accent,
              },
            }}
          >
            {isImporting
              ? t("companyImportExport.importing")
              : t("companyImportExport.import")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default CompanyImportModal;
